import React from 'react';
import { CheckCircle2, PauseCircle, XCircle } from 'lucide-react';
import { getAsset } from '@/client/utils/assets';
import { deriveEntityStatus, entityStatusBadgeClass } from '@/client/utils/filters';
import { formatDate, formatDateTime } from '@/client/utils/datetime';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import type { EntityStatus } from '@/client/api/listing-management/entity.mutation';
import { ActionIconButton } from './ActionIconButton';

const TruncatedTooltip: React.FC<{ text: string; className: string }> = ({ text, className }) => (
  <Tooltip>
    <TooltipTrigger asChild>
      <div className={className}>{text}</div>
    </TooltipTrigger>
    <TooltipContent className="max-w-sm whitespace-normal break-words">{text}</TooltipContent>
  </Tooltip>
);

export const ListingTableRow: React.FC<{
  row: any;
  onOpenDetail: (eid: number) => void;
  onAct: (entity: any, next: EntityStatus, label: string) => void;
}> = ({ row, onOpenDetail, onAct }) => {
  const { Assets, Entity, EntityType, Prices, tags } = row;
  const tagChips = (tags ?? []).slice(0, 3);
  const status = deriveEntityStatus(Entity);
  const title = Entity.name_basic ?? `Listing #${Entity.eid}`;
  const desc = EntityType?.description ?? '';

  return (
    <tr onClick={() => onOpenDetail(Entity.eid)} className="cursor-pointer hover:bg-gray-50">
      <td className="px-6 py-4 whitespace-nowrap">
        <div className="flex items-center">
          <div className="flex-shrink-0 h-12 w-16">
            <img
              className="h-12 w-16 rounded object-cover"
              src={getAsset(Assets?.[0])}
              alt={Assets?.[0]?.name || 'Entity image'}
            />
          </div>
          <div className="ml-4 min-w-0">
            <TruncatedTooltip text={title} className="max-w-[240px] truncate text-sm font-medium text-gray-900" />
            {desc && <TruncatedTooltip text={desc} className="max-w-[240px] truncate text-sm text-gray-500" />}
            {tagChips.length > 0 && (
              <div className="mt-1 flex flex-wrap gap-1">
                {tagChips.map((t: any) => (
                  <span key={t.tagid} className="inline-flex rounded-full bg-slate-100 px-1.5 py-0.5 text-[10px] text-slate-700">
                    {t.name}
                  </span>
                ))}
              </div>
            )}
          </div>
        </div>
      </td>
      <td className="px-6 py-4 whitespace-nowrap">
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
          {EntityType?.name_friendly}
        </span>
      </td>
      <td className="px-6 py-4 whitespace-nowrap">
        {Prices?.[0]?.price ? (
          <div className="text-sm text-gray-900">GHS {Prices?.[0]?.price.toLocaleString()}</div>
        ) : (
          <span className="text-sm text-gray-500">N/A</span>
        )}
      </td>
      <td className="px-6 py-4 whitespace-nowrap">
        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${entityStatusBadgeClass(status)}`}>
          {status.charAt(0) + status.slice(1).toLowerCase()}
        </span>
      </td>
      <td className="px-6 py-4 whitespace-nowrap">
        {Entity.date_added ? (
          <span className="text-sm text-gray-700" title={formatDateTime(Entity.date_added)}>
            {formatDate(Entity.date_added)}
          </span>
        ) : (
          <span className="text-sm text-gray-400">—</span>
        )}
      </td>
      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium" onClick={(ev) => ev.stopPropagation()}>
        <div className="flex items-center justify-end gap-1">
          <ActionIconButton label="Approve (set live)" onClick={() => onAct(Entity, 'LIVE', 'approve')} className="text-green-600 hover:bg-green-50">
            <CheckCircle2 className="h-4 w-4" />
          </ActionIconButton>
          <ActionIconButton label="Reject" onClick={() => onAct(Entity, 'REJECTED', 'reject')} className="text-rose-600 hover:bg-rose-50">
            <XCircle className="h-4 w-4" />
          </ActionIconButton>
          <ActionIconButton label="Suspend" onClick={() => onAct(Entity, 'SUSPENDED', 'suspend')} className="text-orange-600 hover:bg-orange-50">
            <PauseCircle className="h-4 w-4" />
          </ActionIconButton>
        </div>
      </td>
    </tr>
  );
};
